import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Copy, FileText, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function ExportOptionsPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-4">Export Options</h1>
        <p className="text-lg text-gray-700 mb-6">
          AISEOBlogGenerator offers several ways to export your content for use on various platforms. This guide explains all the available export options.
        </p>
      </div>

      <div className="prose max-w-none">
        <h2>Content Export Methods</h2>
        <p>
          Once you've generated a blog post, you'll want to use it on your website, CMS, or other platforms. We provide several convenient export methods to streamline your workflow.
        </p>
      </div>

      <Tabs defaultValue="clipboard">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="clipboard">Copy to Clipboard</TabsTrigger>
          <TabsTrigger value="notion">Notion</TabsTrigger>
          <TabsTrigger value="gdocs">Google Docs</TabsTrigger>
        </TabsList>
        
        <TabsContent value="clipboard" className="pt-6">
          <div className="bg-white border rounded-lg p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Copy className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Copy to Clipboard</h3>
                <p className="text-gray-600 mb-4">
                  The simplest way to export your content is to copy it directly to your clipboard. This method works with any platform that accepts text input.
                </p>
                
                <h4 className="font-medium text-lg mb-3">How to Copy Content</h4>
                <ol className="space-y-3 text-gray-700 list-decimal ml-5">
                  <li>
                    Open the blog post you want to export
                  </li>
                  <li>
                    Click the "Copy Content" button in the top toolbar
                  </li>
                  <li>
                    A notification will appear confirming the content has been copied
                  </li>
                  <li>
                    Paste the content into your destination platform (WordPress, CMS, email, etc.)
                  </li>
                </ol>
                
                <div className="bg-gray-50 p-4 border rounded-md mt-6">
                  <h4 className="font-medium mb-2">Format Preservation</h4>
                  <p className="text-gray-600 text-sm">
                    Content is copied in Markdown format, which preserves your headings, bold text, links, and lists. Most modern editors support Markdown or will convert it to rich text.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="notion" className="pt-6">
          <div className="bg-white border rounded-lg p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 p-3 rounded-lg flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 13 13" fill="currentColor" className="text-gray-800">
                  <path d="M 0.98885 7.96035 L 0.017 6.99179 C -0.08367 6.89199 0.09259 6.34479 0.30193 5.80078 C 0.51342 5.25759 0.71654 4.971 0.81634 5.07167 L 1.78447 6.04023 L 2.752 7.00879 C 2.85267 7.10989 2.6479 7.39472 2.43642 7.9379 C 1.41981 10.2806 -0.20016 11.7347 -0.20016 11.7347 C -0.20016 11.7347 1.25472 10.1242 2.271 7.78085 C 2.48294 7.23767 2.85267 7.85913 2.752 7.96035 L 0.98885 7.96035 Z"></path>
                  <path d="M 2.50595 5.39939 C 2.50595 5.39939 5.09772 2.80804 8.92269 2.80804 C 12.7476 2.80804 12.8483 3.95599 12.8483 4.5069 C 12.8483 5.0578 12.5959 6.5804 11.1465 6.5804 C 9.69732 6.5804 9.62736 5.25759 9.62736 5.25759 L 8.65837 5.25759 L 8.65837 11.4335 L 7.69024 11.4335 C 7.69024 11.4335 0.14993 11.5656 0.14993 11.5656 C 0.14993 11.5656 2.50595 9.73445 2.50595 7.09144 C 2.50595 5.39894 2.50595 5.39894 2.50595 5.39894 L 2.50595 5.39939 Z"></path>
                  <path d="M 3.47402 4.43155 L 2.50595 5.39983 C 2.50595 5.39983 2.50595 1.48464 2.50595 1.04501 C 2.50595 0.60447 2.80797 0.28979 3.47402 0.17365 C 4.14096 0.05797 5.19804 0 5.9027 0 C 6.60736 0 6.77245 0.21353 7.02099 0.74057 C 7.26954 1.2676 8.44532 3.09014 8.44532 3.09014 L 3.47402 3.09014 L 3.47402 4.43155 Z"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Export to Notion</h3>
                <p className="text-gray-600 mb-4">
                  Quickly export your content directly to a new Notion page. This is perfect for users who use Notion for content management or client deliverables.
                </p>
                
                <h4 className="font-medium text-lg mb-3">How to Export to Notion</h4>
                <ol className="space-y-3 text-gray-700 list-decimal ml-5">
                  <li>
                    Open the blog post you want to export
                  </li>
                  <li>
                    Click the "Notion" button in the top toolbar
                  </li>
                  <li>
                    Your content will be copied to clipboard and a new Notion tab will open
                  </li>
                  <li>
                    Paste the content into the new Notion page
                  </li>
                </ol>
                
                <div className="bg-gray-50 p-4 border rounded-md mt-6">
                  <div className="flex items-center gap-2 mb-2">
                    <ExternalLink className="w-4 h-4 text-blue-600" />
                    <h4 className="font-medium">Notion Compatibility</h4>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Notion has excellent support for Markdown, so your content's formatting will be preserved, including headings, bold text, lists, and links.
                  </p>
                </div>
                
                <div className="mt-6 flex justify-center">
                  <img 
                    src="https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?q=80&w=1200&auto=format&fit=crop" 
                    alt="Notion export example" 
                    className="w-full max-w-lg rounded-md shadow-md"
                  />
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="gdocs" className="pt-6">
          <div className="bg-white border rounded-lg p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 p-3 rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-blue-700">
                  <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6z"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Export to Google Docs</h3>
                <p className="text-gray-600 mb-4">
                  Export your content directly to Google Docs, which is perfect for collaboration or if you use Google's suite of tools for your workflow.
                </p>
                
                <h4 className="font-medium text-lg mb-3">How to Export to Google Docs</h4>
                <ol className="space-y-3 text-gray-700 list-decimal ml-5">
                  <li>
                    Open the blog post you want to export
                  </li>
                  <li>
                    Click the "Google Docs" button in the top toolbar
                  </li>
                  <li>
                    Your content will be copied to clipboard and a new Google Docs tab will open
                  </li>
                  <li>
                    Paste the content into the new document
                  </li>
                  <li>
                    Adjust formatting as needed (Google Docs has good but not perfect Markdown support)
                  </li>
                </ol>
                
                <div className="bg-gray-50 p-4 border rounded-md mt-6">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <h4 className="font-medium">Formatting in Google Docs</h4>
                  </div>
                  <p className="text-gray-600 text-sm">
                    While Google Docs doesn't natively support Markdown, our export process is optimized to maintain the structure of your content. You may need to make minor formatting adjustments after pasting.
                  </p>
                </div>
                
                <div className="bg-yellow-50 p-4 border border-yellow-100 rounded-md mt-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Tip:</strong> For best results in Google Docs, after pasting, use the "Clear formatting" option and then apply Google Docs' native heading styles and formatting.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="bg-gray-50 border rounded-lg p-6 mt-8">
        <h3 className="text-xl font-bold mb-4">Advanced Export Tips</h3>
        
        <div className="space-y-6">
          <div>
            <h4 className="font-medium text-lg mb-2">Preserving SEO Elements</h4>
            <p className="text-gray-700 mb-3">
              When exporting to your CMS or website, pay attention to these important SEO elements:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600">
              <li>
                <strong>Meta Description:</strong> Every generated post includes an HTML comment at the top with a suggested meta description.
              </li>
              <li>
                <strong>Heading Structure:</strong> Maintain the H1, H2, and H3 hierarchy as generated.
              </li>
              <li>
                <strong>Internal Links:</strong> Add internal links to other relevant content on your site.
              </li>
              <li>
                <strong>Images:</strong> Add relevant images with descriptive alt text for better SEO.
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium text-lg mb-2">Content Customization Before Export</h4>
            <p className="text-gray-700 mb-3">
              Consider these adjustments before exporting your content:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600">
              <li>
                Add brand-specific terminology or messaging
              </li>
              <li>
                Insert custom call-to-action sections
              </li>
              <li>
                Update examples to match your specific industry or use cases
              </li>
              <li>
                Incorporate company data or case studies to strengthen the content
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8 pt-6 border-t">
        <Button variant="outline" asChild>
          <Link to="/docs/posts/managing">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back: Managing Posts
          </Link>
        </Button>
        <Button asChild>
          <Link to="/docs/strike-list">
            Next: Strike List
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}